import { Injectable } from '@angular/core';
import { ComplianceCheckerService } from '../compliance-checker/compliance-checker.service';
import { ExcelFileParserService } from '../excel-file-parser/excel-file-parser.service';
import { HyperFormulaParserService } from '../hyper-formula-parser/hyper-formula-parser.service';
import { ResultDataService } from '../result-data/result-data.service';
import { StructuredInputService } from '../structured-input/structured-input.service';
import { catchError, map, Observable, Subject, take, throwError } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { FileService } from '../file/file.service';
import { HyperFormulaDataService } from '../hyper-formula-data/hyper-formula-data.service';

@Injectable({
  providedIn: 'root',
})
export class OrchestratorService {
  initializeCompleted$ = new Subject<void>();
  recalculateScenarioDataFromFormCompleted$ = new Subject<void>();

  constructor(
    private _excelFileParser: ExcelFileParserService,
    private _hfParser: HyperFormulaParserService,
    private _complianceChecker: ComplianceCheckerService,
    private _resultDataService: ResultDataService,
    private _structuredInputService: StructuredInputService,
    private _fileService: FileService,
    private hf: HyperFormulaDataService,
  ) {}

  initialize(fileId: string): Observable<void> {
    const file = this._fileService.getFile(fileId);
    this._excelFileParser.loadFile(file);
    return this._excelFileParser.loadFileComplete$.pipe(
      take(1),
      map(() => {
        this._hfParser.parseExcelSheets(
          this._excelFileParser.retrievedSheets,
          this._excelFileParser.namedRanges,
        );
        this._structuredInputService.convertRowsToObjects();

        const resultRows = this._structuredInputService.getResultRows();
        const scenarioRows = this._structuredInputService.getScenarioRows();
        const carePathwayRows =
          this._structuredInputService.getCarePathwayRows();
        const introRows = this._structuredInputService.getIntroRows();
        const configRows = this._structuredInputService.getConfigRows();
        const summaryRows = this._structuredInputService.getSummaryRows();

        this._complianceChecker.checkParsedFileCompliance(
          resultRows,
          scenarioRows,
          carePathwayRows,
          introRows,
          configRows,
          summaryRows,
        );

        this._resultDataService.reformatResultData(
          resultRows,
          scenarioRows,
          carePathwayRows,
          introRows,
          configRows,
          summaryRows,
        );

        this.initializeCompleted$.next();
      }),
      catchError((err: Error) => {
        console.error(err);
        return throwError(() => new Error(err.message));
      }),
    );
  }

  runComplianceCheckOnFile(file: File) {
    this._excelFileParser.loadFromFileObject(file);
    return this._excelFileParser.loadFileComplete$.pipe(
      take(1),
      map(() => {
        this._hfParser.parseExcelSheets(
          this._excelFileParser.retrievedSheets,
          this._excelFileParser.namedRanges,
        );
        this._structuredInputService.convertRowsToObjects();
        this._complianceChecker.checkParsedFileCompliance(
          this._structuredInputService.getResultRows(),
          this._structuredInputService.getScenarioRows(),
          this._structuredInputService.getCarePathwayRows(),
          this._structuredInputService.getIntroRows(),
          this._structuredInputService.getConfigRows(),
          this._structuredInputService.getSummaryRows(),
        );
      }),
      catchError((err: Error) => {
        return throwError(() => new Error(err.message));
      }),
    );
  }

  generateScenario(form: FormGroup): void {
    this._updateScenarioRowsFromForm(form);
    this._structuredInputService.convertRowsToObjects();
    this._resultDataService.reformatResultData(
      this._structuredInputService.getResultRows(),
      this._structuredInputService.getScenarioRows(),
      this._structuredInputService.getCarePathwayRows(),
      this._structuredInputService.getIntroRows(),
      this._structuredInputService.getConfigRows(),
      this._structuredInputService.getSummaryRows(),
    );
  }

  private _updateScenarioRowsFromForm(form: FormGroup): void {
    const scenarioRows = this._structuredInputService.getScenarioRows();

    // Build stable keys from the scenario sheet: "Category::Name"
    // Carry forward last non-empty category to match how controls are named in the UI
    const keyToIndex = new Map<string, number>();
    let currentCategory = '';
    scenarioRows.forEach((row, idx) => {
      if (row.category) {
        currentCategory = row.category;
      }
      const key = `${currentCategory}::${row.name}`.trim();
      keyToIndex.set(key, idx);
    });

    Object.keys(form.controls).forEach((controlKey) => {
      let idx = keyToIndex.get(controlKey);

      // Fallback to plain name if a control was not found (e.g. older files)
      if (idx == null) {
        idx = scenarioRows.findIndex((r) => r.name === controlKey);
      }

      if (idx == null) {
        console.warn(`Scenario control '${controlKey}' not found in rows`);
        return;
      }

      const scenarioRow = scenarioRows[idx];
      // Preserve formulas by skipping non-modifiable rows
      if (scenarioRow && !scenarioRow.modifiable) {
        return;
      }

      const control = form.get([controlKey]);
      const value = control?.value;
      if (value === null || value === undefined) {
        console.warn(
          `Scenario control '${controlKey}' has null/undefined value; skipping write`,
        );
        return;
      }

      this._structuredInputService.updateScenarioRow(idx, value);
    });
  }
}
